import { format, isSameYear, isToday, isYesterday } from "date-fns";
import { Bot, Settings, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  ACTIVITY_EVENT_ACTOR_TYPE,
  CONVERSION_UNIT_ABBREVIATION,
  ConversionUnitSchema,
  CostingMethodSchema,
  DIMENSION_UNIT_ABBREVIATION,
  ItemTypeSchema,
  WEIGHT_UNIT_ABBREVIATION,
  formatQuoteNumber,
  operationRowKey,
} from "@forgepoint/shared";
import type {
  ActivityEvent,
  HydratedItem,
  ItemActivityEvent,
  ItemFieldChange,
  ItemMargin,
  ItemUnitConversion,
  PublicUser,
} from "@forgepoint/shared";
import { formatMoney } from "@/lib/formatMoney";
import { keyToLabel } from "@/lib/utils";
import { getUserDisplayName, getUserInitials } from "@/store/teamsStore";
import { COSTING_METHOD_LABELS, ITEM_TYPE_LABELS } from "./itemEnumLabels";
import {
  draftDirtyKeys,
  recordFieldEdits,
  type ItemDraft,
} from "./itemDraftsStore";
import type { ItemFormData } from "./itemFormData";

/** The date the item change log began recording. */
export const ITEM_HISTORY_RECORDED_SINCE = "2026-09-23";

/** One field's old and new value, from a recorded event or an unsaved draft. */
export type ItemHistoryChange = Pick<ItemFieldChange, "field" | "from" | "to">;

/** The form tab and field wrapper a history row points at. */
export interface ItemFieldTarget {
  tab: "details" | "bom";
  anchor: string | null;
  note?: string;
}

/** The names a change needs to read as a sentence, resolved by the caller. */
export interface ItemHistoryContext {
  item: HydratedItem | null;
  operationNames: ReadonlyMap<string, string>;
  itemNames: ReadonlyMap<string, string>;
  fileNames: ReadonlyMap<string, string>;
  priceModelNames: ReadonlyMap<string, string>;
}

/** How an actor's avatar is tinted. */
export type ItemHistoryActorTone =
  | "user"
  | "agent"
  | "machine"
  | "draft"
  | "external";

/** A person shown beside an actor: the user an agent write was for. */
export interface ItemHistoryPerson {
  name: string;
  initials: string;
  imageUrl: string | null;
}

/** Who made a write, as the History line renders it. */
export interface ItemHistoryActor {
  name: string;
  icon: LucideIcon;
  initials: string | null;
  imageUrl: string | null;
  tone: ItemHistoryActorTone;
  context: string | null;
  behalf: ItemHistoryPerson | null;
}

/** One day's writes, newest first. */
export interface ItemHistoryDayGroup {
  key: string;
  label: string;
  events: ItemActivityEvent[];
}

// Only labels that differ from keyToLabel of the key.
const FIELD_LABELS: Record<string, string> = {
  name: "Name / Item Number",
  weight: "Weight per Unit",
  priceModelId: "Price Model",
};
const ANCHORED_DETAIL_FIELDS: ReadonlySet<string> = new Set([
  "name",
  "revision",
  "type",
  "costingMethod",
  "unit",
  "weight",
  "weightUnit",
  "dimensionUnit",
  "length",
  "width",
  "height",
  "allowance",
  "description",
]);
// Fields older builds recorded that are not history.
const HIDDEN_FIELDS: ReadonlySet<string> = new Set(["reviewStatus"]);
const MONEY_FIELDS: ReadonlySet<string> = new Set([
  "unitCost",
  "overrideOperationCost",
  "overrideSetupRate",
  "minimumCharge",
  "overrideComponentCost",
]);
const PERCENT_FIELDS: ReadonlySet<string> = new Set([
  "percentage",
  "efficiency",
  "scrapPercent",
]);
const DIMENSION_FIELDS: ReadonlySet<string> = new Set([
  "length",
  "width",
  "height",
  "allowance",
]);

/**
 * The label for a scalar field key: a column, a cost break, a custom field, a
 * price model input, or a unit conversion rate.
 * @param field - The change's field key
 * @returns A human label, never a raw dotted key
 */
export function fieldLabel(field: string): string {
  const [root, ...rest] = field.split(".");
  if (root === "costBreaks" && rest.length > 0) {
    return rest[0] === "1" ? "Unit Cost" : `Unit Cost, ${rest[0]}+`;
  }
  if (root === "customFields" && rest.length > 0) {
    return rest.join(".");
  }
  if (root === "priceModelInputs" && rest.length > 0) {
    return keyToLabel(rest.join(" "));
  }
  if (root === "unitConversions" && rest.length === 2) {
    const [from, to] = rest.map(unitAbbreviation);
    return `Unit conversion (1 ${from} → ${to})`;
  }
  return FIELD_LABELS[field] ?? keyToLabel(field.split(".").join(" "));
}

/**
 * Formats a value recorded under a field key for display.
 * @param field - The key the value sits under; its last segment picks money,
 *   percent, or unit formatting
 * @param value - The recorded value
 * @param context - The item and the names ids resolve through
 * @returns The display string, or "None" when empty
 */
export function formatFieldValue(
  field: string,
  value: unknown,
  context: ItemHistoryContext,
): string {
  if (isBlank(value)) {
    return "None";
  }
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(", ");
  }
  if (isRecord(value)) {
    return formatRecordValue(value);
  }
  const text = String(value);
  if (field === "priceModelId") {
    return context.priceModelNames.get(text) ?? "Price model";
  }
  const enumLabel = enumValueLabel(field, text);
  if (enumLabel) {
    return enumLabel;
  }
  const last = field.split(".").at(-1) ?? "";
  if (field.startsWith("costBreaks.") || MONEY_FIELDS.has(last)) {
    return formatMoney(text);
  }
  if (field.startsWith("yield.") || PERCENT_FIELDS.has(last)) {
    return `${text}%`;
  }
  const suffix = unitSuffix(field, context.item);
  return suffix ? `${text} ${suffix}` : text;
}

/**
 * The operation name behind an operation row key.
 * @param rowKey - The operation row key, or an older event's row id or list position
 * @param change - A change on that row, whose value may carry the operation id
 * @param context - The item and the names ids resolve through
 * @returns The operation name, or "Operation" when nothing resolves
 */
export function operationRowName(
  rowKey: string,
  change: ItemHistoryChange,
  context: ItemHistoryContext,
): string {
  const operationId =
    readOperationId(change.to) ??
    readOperationId(change.from) ??
    operationIdFromItem(rowKey, context.item);
  return (
    (operationId ? context.operationNames.get(operationId) : null) ??
    "Operation"
  );
}

/**
 * The form tab and field wrapper a change jumps to.
 * @param change - The change being rendered
 * @param item - The item, which resolves an older event's operation row key
 * @returns The target tab, and the field anchor when the form shows the field
 */
export function fieldAnchorForChange(
  change: ItemHistoryChange,
  item: HydratedItem | null,
): ItemFieldTarget {
  const [root, ...rest] = change.field.split(".");
  if (root === "operations") {
    return { tab: "bom", anchor: operationAnchor(rest[0], item) };
  }
  if (root === "components") {
    return { tab: "bom", anchor: `${root}.${rest[0]}` };
  }
  if (root === "upgrades") {
    return { tab: "bom", anchor: null };
  }
  if (root === "customFields" || root === "costBreaks") {
    return { tab: "details", anchor: change.field };
  }
  if (root === "unitConversions") {
    return { tab: "details", anchor: root };
  }
  if (rest.length === 0 && ANCHORED_DETAIL_FIELDS.has(root)) {
    return { tab: "details", anchor: root };
  }
  return { tab: "details", anchor: null };
}

/**
 * Groups events into day buckets, newest first. Events with no visible change
 * are dropped, so a day of only hidden changes has no group.
 * @param events - The item's events
 * @param now - Reference point for "Today" and "Yesterday"
 * @returns The day groups, each holding its events newest first
 */
export function groupEventsByDay(
  events: readonly ItemActivityEvent[],
  now: Date = new Date(),
): ItemHistoryDayGroup[] {
  const shown = events.filter(
    (event) => visibleChanges(event.eventMetadata.changes).length > 0,
  );
  const sorted = shown.sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  );
  const byDay = new Map<string, ItemActivityEvent[]>();
  for (const event of sorted) {
    const key = format(event.createdAt, "yyyy-MM-dd");
    byDay.set(key, [...(byDay.get(key) ?? []), event]);
  }
  return [...byDay.entries()].map(([key, dayEvents]) => ({
    key,
    label: dayLabel(dayEvents[0].createdAt, now),
    events: dayEvents,
  }));
}

/**
 * Narrows an activity event to an item event.
 * @param event - Any activity event
 * @returns Whether the event was recorded against an item
 */
export function isItemEvent(event: ActivityEvent): event is ItemActivityEvent {
  return event.entityType === "ITEM";
}

/**
 * Drops the changes the History tab never shows.
 * @param changes - An event's recorded changes
 * @returns The changes to render
 */
export function visibleChanges<T extends ItemHistoryChange>(
  changes: readonly T[],
): T[] {
  return changes.filter((change) => !HIDDEN_FIELDS.has(change.field));
}

/**
 * Who made a write, and how the History line tints and labels them. An agent
 * write made for a user also names that user; a write from a file, a sync or a
 * quote names it as context.
 * @param event - The event being rendered
 * @param agentName - The standard agent's display name
 * @param members - Org members by user id
 * @param padWidth - The org's quote number pad width
 * @param prefix - The org's quote number prefix
 * @returns The actor line
 */
export function actorLine(
  event: ItemActivityEvent,
  agentName: string,
  members: ReadonlyMap<string, PublicUser>,
  padWidth: number | null,
  prefix: string | null,
): ItemHistoryActor {
  const context = writeContext(event, padWidth, prefix);
  if (event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.SYSTEM) {
    return {
      name: "System",
      icon: Settings,
      initials: null,
      imageUrl: null,
      tone: "machine",
      context,
      behalf: null,
    };
  }
  if (event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.AGENT_SDK_THREAD) {
    return {
      name: agentName,
      icon: Bot,
      initials: null,
      imageUrl: null,
      tone: "agent",
      context,
      behalf: event.userId ? memberPerson(event.userId, null, members) : null,
    };
  }
  const person = memberPerson(event.actorId, event.actorName, members);
  return {
    ...person,
    icon: User,
    tone: "user",
    context,
    behalf: null,
  };
}

/**
 * The ids a change log mentions whose names the item may no longer hold, so
 * the tab can fetch them.
 * @param changes - The changes being rendered
 * @returns The referenced item, operation, and asset ids, each deduped
 */
export function referencedEntityIds(changes: readonly ItemHistoryChange[]): {
  itemIds: string[];
  operationIds: string[];
  assetIds: string[];
} {
  const itemIds = changes.flatMap((change) => {
    const [root, key] = change.field.split(".");
    return (root === "components" || root === "upgrades") && key ? [key] : [];
  });
  const operationIds = changes.flatMap((change) => {
    if (!change.field.startsWith("operations.")) {
      return [];
    }
    const operationId =
      readOperationId(change.to) ?? readOperationId(change.from);
    return operationId ? [operationId] : [];
  });
  const assetIds = changes.flatMap((change) => {
    if (change.field.startsWith("files.assets.")) {
      return [change.field.slice("files.assets.".length)];
    }
    if (change.field === "files.primary") {
      return [change.from, change.to].filter(
        (id): id is string => typeof id === "string",
      );
    }
    return [];
  });
  return {
    itemIds: [...new Set(itemIds)],
    operationIds: [...new Set(operationIds)],
    assetIds: [...new Set(assetIds)],
  };
}

/**
 * The unsaved edits in a draft, keyed the way the backend diff keys them so
 * they group into the same rows.
 * @param draft - The item's draft, or undefined when none is open
 * @returns One change per edited field or list entry
 */
export function draftChanges(
  draft: ItemDraft | undefined,
): ItemHistoryChange[] {
  if (!draft) {
    return [];
  }
  return draftDirtyKeys(draft).flatMap((key) => {
    if (key === "customFields" || key === "priceModelInputs") {
      return recordDraftChanges(draft, key);
    }
    if (key === "costBreaks") {
      return keyedDraftChanges(
        "costBreaks",
        unitCostsByQuantity(draft.baseline.costBreaks),
        unitCostsByQuantity(draft.values.costBreaks ?? []),
      );
    }
    if (key === "margin") {
      return keyedDraftChanges(
        "margin",
        marginsByTier(draft.baseline.margin),
        marginsByTier(draft.values.margin),
      );
    }
    if (key === "unitConversions") {
      return keyedDraftChanges(
        "unitConversions",
        ratesByUnitPair(draft.baseline.unitConversions),
        ratesByUnitPair(draft.values.unitConversions),
      );
    }
    if (key === "fileAssetIds") {
      return keyedDraftChanges(
        "files.assets",
        idMap(draft.baseline.fileAssetIds),
        idMap(draft.values.fileAssetIds),
      );
    }
    if (key === "organizationFileIds") {
      return keyedDraftChanges(
        "files.vault",
        idMap(draft.baseline.organizationFileIds),
        idMap(draft.values.organizationFileIds),
      );
    }
    const field = key === "primaryFileAssetId" ? "files.primary" : key;
    return [
      {
        field,
        from: draft.baseline[key] ?? null,
        to: draft.values[key] ?? null,
      },
    ];
  });
}

/**
 * True for a value the log treats as absent.
 * @param value - Any recorded value
 * @returns Whether the value is null, undefined, or an empty string
 */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

/**
 * Narrows an unknown value to a plain object.
 * @param value - Any recorded value
 * @returns Whether the value is a non-array object
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readOperationId(value: unknown): string | null {
  if (!isRecord(value)) {
    return null;
  }
  return typeof value.operationId === "string" ? value.operationId : null;
}

// The display name for an item type or costing method; null for anything else.
function enumValueLabel(field: string, value: string): string | null {
  if (field === "type") {
    const type = ItemTypeSchema.safeParse(value);
    return type.success ? ITEM_TYPE_LABELS[type.data] : null;
  }
  if (field === "costingMethod") {
    const method = CostingMethodSchema.safeParse(value);
    return method.success ? COSTING_METHOD_LABELS[method.data] : null;
  }
  return null;
}

// A margin amount, or a short summary of any other recorded object.
function formatRecordValue(value: Record<string, unknown>): string {
  if (typeof value.percentage === "string") {
    return `${value.percentage}%`;
  }
  if (typeof value.amount === "string") {
    return formatMoney(value.amount);
  }
  if (typeof value.formula === "string") {
    return value.formula;
  }
  return Object.entries(value)
    .filter(([, entry]) => !isBlank(entry))
    .slice(0, 3)
    .map(([key, entry]) => `${keyToLabel(key)}: ${String(entry)}`)
    .join(" · ");
}

function operationIdFromItem(
  rowKey: string,
  item: HydratedItem | null,
): string | null {
  if (!isLegacyOperationKey(rowKey)) {
    return rowKey.split("~")[0];
  }
  const rows = item?.operations ?? [];
  return rows[legacyOperationIndex(rowKey, rows)]?.operationId ?? null;
}

// Null when an older event's row is no longer on the item.
function operationAnchor(
  rowKey: string,
  item: HydratedItem | null,
): string | null {
  if (!isLegacyOperationKey(rowKey)) {
    return `operations.${rowKey}`;
  }
  const rows = item?.operations ?? [];
  const index = legacyOperationIndex(rowKey, rows);
  if (index === -1) {
    return null;
  }
  return `operations.${operationRowKey(rows[index], index, rows)}`;
}

// Older events key operation rows by `iop_` row id or by list position.
function isLegacyOperationKey(rowKey: string): boolean {
  return rowKey.startsWith("iop_") || /^\d+$/.test(rowKey);
}

function legacyOperationIndex(
  rowKey: string,
  rows: readonly { id?: string }[],
): number {
  const byId = rows.findIndex((row) => row.id === rowKey);
  if (byId !== -1) {
    return byId;
  }
  const index = Number(rowKey);
  return Number.isInteger(index) && index < rows.length ? index : -1;
}

function unitAbbreviation(unit: string): string {
  const parsed = ConversionUnitSchema.safeParse(unit);
  return parsed.success ? CONVERSION_UNIT_ABBREVIATION[parsed.data] : unit;
}

function unitSuffix(field: string, item: HydratedItem | null): string | null {
  if (field === "weight" && item?.weightUnit) {
    return WEIGHT_UNIT_ABBREVIATION[item.weightUnit];
  }
  if (DIMENSION_FIELDS.has(field) && item?.dimensionUnit) {
    return DIMENSION_UNIT_ABBREVIATION[item.dimensionUnit];
  }
  return null;
}

/**
 * A day divider's label: "Today", "Yesterday", or the date.
 * @param date - A moment in the day
 * @param now - Reference point for "Today" and "Yesterday"
 * @returns The label, with the year only when it is not this year's
 */
export function dayLabel(date: Date, now: Date): string {
  if (isToday(date)) {
    return "Today";
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }
  return isSameYear(date, now)
    ? format(date, "MMM d")
    : format(date, "MMM d, yyyy");
}

// What the write came from, when the event records it: a file, a sync or a quote.
function writeContext(
  event: ItemActivityEvent,
  padWidth: number | null,
  prefix: string | null,
): string | null {
  const { fileName, syncId, quoteNumber } = event.eventMetadata;
  if (fileName) {
    return `imported from ${fileName}`;
  }
  if (syncId) {
    return `ERP sync ${syncId}`;
  }
  if (quoteNumber !== undefined) {
    return `Quote #${formatQuoteNumber(quoteNumber, padWidth, prefix)}`;
  }
  return null;
}

/**
 * A member's name, initials and photo; a recorded name when the member is not
 * in the org list.
 * @param userId - The member's user id, when known
 * @param recordedName - The name the event recorded, used as the fallback
 * @param members - Org members by user id
 * @returns The person as a History line shows them
 */
export function memberPerson(
  userId: string | null,
  recordedName: string | null,
  members: ReadonlyMap<string, PublicUser>,
): ItemHistoryPerson {
  const member = userId ? members.get(userId) : undefined;
  if (member) {
    return {
      name: getUserDisplayName(member),
      initials: getUserInitials(member),
      imageUrl: member.profilePictureUrl ?? null,
    };
  }
  const name = recordedName ?? "Unknown user";
  return { name, initials: initialsOf(name), imageUrl: null };
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function recordDraftChanges(
  draft: ItemDraft,
  key: "customFields" | "priceModelInputs",
): ItemHistoryChange[] {
  const baseline: Record<string, unknown> = draft.baseline[key] ?? {};
  return Object.entries(recordFieldEdits(draft, key)).map(
    ([subKey, value]) => ({
      field: `${key}.${subKey}`,
      from: baseline[subKey] ?? null,
      to: value,
    }),
  );
}

// One change per key whose value differs, keyed `<prefix>.<key>`.
function keyedDraftChanges(
  prefix: string,
  baseline: ReadonlyMap<string, unknown>,
  edited: ReadonlyMap<string, unknown>,
): ItemHistoryChange[] {
  const keys = [...new Set([...baseline.keys(), ...edited.keys()])];
  return keys
    .filter(
      (key) =>
        JSON.stringify(baseline.get(key) ?? null) !==
        JSON.stringify(edited.get(key) ?? null),
    )
    .map((key) => ({
      field: `${prefix}.${key}`,
      from: baseline.get(key) ?? null,
      to: edited.get(key) ?? null,
    }));
}

function unitCostsByQuantity(
  costBreaks: ItemFormData["costBreaks"],
): ReadonlyMap<string, unknown> {
  return new Map(
    costBreaks.map((costBreak) => [
      String(costBreak.minQty),
      costBreak.unitCost,
    ]),
  );
}

// Keyed the way the backend diff keys margins: `<type>.<minQty or default>`.
function marginsByTier(
  margins: readonly ItemMargin[] | undefined,
): ReadonlyMap<string, unknown> {
  return new Map(
    (margins ?? []).map((margin) => [
      `${margin.type}.${margin.minQty ?? "default"}`,
      margin.marginAmount,
    ]),
  );
}

// Keyed the way the backend diff keys rates: `<fromUnit>.<toUnit>`.
function ratesByUnitPair(
  conversions: readonly ItemUnitConversion[] | undefined,
): ReadonlyMap<string, unknown> {
  return new Map(
    (conversions ?? []).map((conversion) => [
      `${conversion.fromUnit}.${conversion.toUnit}`,
      conversion.rate,
    ]),
  );
}

function idMap(
  ids: readonly string[] | undefined,
): ReadonlyMap<string, unknown> {
  return new Map((ids ?? []).map((id) => [id, id]));
}
